import React, { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { inr } from "../lib/currency";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Minus, Plus, LogOut, Save, Tag, Trash2, Wallet } from "lucide-react";
import { toast } from "sonner";

export const AdminDashboard = () => {
  const { auth, logout } = useAuth();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [query, setQuery] = useState("");
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    api.get(`/products`).then((r) => setProducts(r.data));
  }, []);

  const q = query.trim().toLowerCase();
  const visible = useMemo(
    () =>
      q
        ? products.filter(
            (p) =>
              p.name.toLowerCase().includes(q) ||
              p.category.toLowerCase().includes(q)
          )
        : products,
    [q, products]
  );

  if (!auth || auth.role !== "admin") return <Navigate to="/admin/login" replace />;

  const patch = async (id, body) => {
    setBusyId(id);
    try {
      const { data } = await api.patch(`/admin/products/${id}`, body);
      setProducts((prev) => prev.map((p) => (p.id === id ? data : p)));
      return data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally {
      setBusyId(null);
    }
  };

  const doSignout = () => {
    logout();
    navigate("/admin/login");
  };

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-background">
      <header className="sticky top-0 z-30 bg-background/85 backdrop-blur border-b border-border">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/" className="font-display text-xl font-semibold">
              Village<span className="text-accent">·</span>Farm<span className="text-accent">·</span>Food
            </Link>
            <span className="px-2.5 py-1 rounded-full bg-primary text-primary-foreground text-[10px] font-mono-ui uppercase tracking-widest">
              Admin
            </span>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline text-sm text-muted-foreground">
              Signed in as <strong>{auth.username}</strong>
            </span>
            <Button
              data-testid="admin-logout"
              onClick={doSignout}
              variant="outline"
              className="h-9 rounded-full"
            >
              <LogOut className="w-4 h-4 mr-2" /> Sign out
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
          <div>
            <div className="font-mono-ui text-xs uppercase tracking-widest text-accent mb-2">
              Store Manager
            </div>
            <h1 className="font-display text-4xl md:text-5xl font-semibold leading-tight">
              Prices &amp; Offers
            </h1>
            <p className="mt-2 text-sm text-muted-foreground max-w-xl">
              Adjust prices, run limited-time offers with a % discount, tag them
              with a festive label — the storefront updates instantly.
            </p>
          </div>
          <div>
            <Input
              data-testid="admin-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search products or category..."
              className="h-11 w-full md:w-80"
            />
          </div>
        </div>

        <Tabs defaultValue="prices" className="w-full">
          <TabsList className="grid w-full md:w-[420px] grid-cols-2">
            <TabsTrigger value="prices" data-testid="admin-tab-prices">
              <Wallet className="w-3.5 h-3.5 mr-2" /> Prices
            </TabsTrigger>
            <TabsTrigger value="offers" data-testid="admin-tab-offers">
              <Tag className="w-3.5 h-3.5 mr-2" /> Offers
            </TabsTrigger>
          </TabsList>

          <TabsContent value="prices" className="mt-5">
            <PriceTable
              products={visible}
              busyId={busyId}
              onDelta={(id, d) => patch(id, { delta: d }).then((p) => p && toast.success(`${p.name} → ${inr(p.price)}`))}
              onSet={(id, price) => patch(id, { price }).then((p) => p && toast.success(`${p.name} → ${inr(p.price)}`))}
            />
          </TabsContent>

          <TabsContent value="offers" className="mt-5">
            <BulkCategoryOffer
              onApplied={() => api.get(`/products`).then((r) => setProducts(r.data))}
            />
            <OffersTable
              products={visible}
              busyId={busyId}
              onSave={(id, body) => patch(id, body).then((p) => p && toast.success(`Offer saved on ${p.name}`))}
              onClear={(id) =>
                patch(id, { discount_percent: 0, offer_label: "", offer_expires_at: "" }).then(
                  (p) => p && toast.success(`Offer cleared on ${p.name}`)
                )
              }
            />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

const PriceTable = ({ products, busyId, onDelta, onSet }) => (
  <div className="overflow-x-auto border border-border rounded-2xl bg-card">
    <table className="w-full text-sm">
      <thead className="bg-secondary/50 text-left">
        <tr className="font-mono-ui text-[10px] uppercase tracking-widest text-muted-foreground">
          <th className="py-3 px-4">Product</th>
          <th className="py-3 px-4">Category</th>
          <th className="py-3 px-4">Unit</th>
          <th className="py-3 px-4 text-right">Current price</th>
          <th className="py-3 px-4 text-right">Adjust</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-border">
        {products.map((p) => (
          <PriceRow
            key={p.id}
            product={p}
            busy={busyId === p.id}
            onDelta={(d) => onDelta(p.id, d)}
            onSet={(price) => onSet(p.id, price)}
          />
        ))}
      </tbody>
    </table>
  </div>
);

const PriceRow = ({ product, busy, onDelta, onSet }) => {
  const [draft, setDraft] = useState("");
  const saveDraft = () => {
    const inrVal = parseFloat(draft);
    if (isNaN(inrVal) || inrVal < 0) return toast.error("Enter a valid ₹ amount");
    onSet(+(inrVal / 80).toFixed(2));
    setDraft("");
  };
  return (
    <tr data-testid={`admin-row-${product.id}`}>
      <td className="py-3 px-4">
        <div className="flex items-center gap-3">
          <img src={product.image} alt="" className="w-10 h-10 rounded-lg object-cover shrink-0" />
          <div className="font-display font-semibold leading-tight">{product.name}</div>
        </div>
      </td>
      <td className="py-3 px-4 text-muted-foreground capitalize">{product.category}</td>
      <td className="py-3 px-4 font-mono-ui text-xs text-muted-foreground uppercase tracking-wider">
        {product.unit}
      </td>
      <td
        data-testid={`admin-price-${product.id}`}
        className="py-3 px-4 text-right font-mono-ui text-base font-semibold text-primary"
      >
        {inr(product.price)}
      </td>
      <td className="py-3 px-4">
        <div className="flex items-center gap-2 justify-end">
          <button
            data-testid={`admin-dec-${product.id}`}
            onClick={() => onDelta(-0.5)}
            disabled={busy}
            className="w-9 h-9 rounded-full border border-border hover:bg-destructive hover:text-destructive-foreground hover:border-destructive transition-colors flex items-center justify-center disabled:opacity-50"
            aria-label="Decrease price ₹40"
          >
            <Minus className="w-4 h-4" />
          </button>
          <button
            data-testid={`admin-inc-${product.id}`}
            onClick={() => onDelta(0.5)}
            disabled={busy}
            className="w-9 h-9 rounded-full border border-border hover:bg-accent hover:text-accent-foreground hover:border-accent transition-colors flex items-center justify-center disabled:opacity-50"
            aria-label="Increase price ₹40"
          >
            <Plus className="w-4 h-4" />
          </button>
          <Input
            data-testid={`admin-set-${product.id}`}
            type="number"
            min="0"
            step="10"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Set ₹"
            className="h-9 w-24"
          />
          <button
            data-testid={`admin-save-${product.id}`}
            onClick={saveDraft}
            disabled={busy || !draft}
            className="w-9 h-9 rounded-full bg-primary text-primary-foreground hover:bg-accent hover:text-accent-foreground transition-colors flex items-center justify-center disabled:opacity-50"
            aria-label="Save price"
          >
            <Save className="w-4 h-4" />
          </button>
        </div>
      </td>
    </tr>
  );
};

const OffersTable = ({ products, busyId, onSave, onClear }) => (
  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
    {products.map((p) => (
      <OfferCard
        key={p.id}
        product={p}
        busy={busyId === p.id}
        onSave={(body) => onSave(p.id, body)}
        onClear={() => onClear(p.id)}
      />
    ))}
  </div>
);

const CATEGORIES = [
  "ghee", "milk", "makhan", "oil", "vegetables", "mushroom",
  "spices", "seeds", "aata", "dryfruits", "coconut", "aachar",
  "tea", "rajasthani", "laddu",
];

const BulkCategoryOffer = ({ onApplied }) => {
  const [category, setCategory] = useState("spices");
  const [discount, setDiscount] = useState(10);
  const [label, setLabel] = useState("Diwali Special");
  const [starts, setStarts] = useState("");
  const [expires, setExpires] = useState("");
  const [busy, setBusy] = useState(false);

  const apply = async () => {
    const dp = parseFloat(discount);
    if (isNaN(dp) || dp < 0 || dp > 90) return toast.error("Discount must be 0-90%");
    if (starts && expires && starts > expires)
      return toast.error("Start date must be before expiry");
    setBusy(true);
    try {
      const { data } = await api.patch(`/admin/categories/${category}/offer`, {
        discount_percent: dp,
        offer_label: label,
        offer_starts_at: starts,
        offer_expires_at: expires,
      });
      const scheduled = starts && starts > new Date().toISOString().slice(0, 10)
        ? " (scheduled to auto-start)"
        : "";
      toast.success(`${data.updated} products in "${category}" now ${dp}% off${scheduled}`);
      onApplied?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk offer failed");
    } finally {
      setBusy(false);
    }
  };

  const clear = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/admin/categories/${category}/offer`, {
        discount_percent: 0,
        offer_label: "",
        offer_starts_at: "",
        offer_expires_at: "",
      });
      toast.success(`Cleared offers on ${data.updated} products in "${category}"`);
      onApplied?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Clear failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="bulk-category-offer"
      className="mb-8 rounded-2xl border-2 border-dashed border-accent/50 bg-accent/5 p-5 md:p-6"
    >
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
        <div>
          <div className="font-mono-ui text-[10px] uppercase tracking-widest text-accent">
            Bulk Category Offer
          </div>
          <h3 className="font-display text-2xl font-semibold leading-tight">
            One-click discount for a whole category
          </h3>
          <p className="text-sm text-muted-foreground mt-1">
            Great for festival campaigns — pick a category and apply the same
            discount, label and expiry to every product inside it.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <div>
          <Label className="text-[10px] uppercase tracking-widest font-mono-ui">Category</Label>
          <select
            data-testid="bulk-category"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="mt-1 w-full h-10 rounded-md border border-input bg-background px-3 text-sm capitalize"
          >
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-widest font-mono-ui">Discount %</Label>
          <Input
            data-testid="bulk-discount"
            type="number"
            min="0"
            max="90"
            step="1"
            value={discount}
            onChange={(e) => setDiscount(e.target.value)}
            className="mt-1 h-10"
          />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-widest font-mono-ui">Label</Label>
          <Input
            data-testid="bulk-label"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="Diwali Special"
            className="mt-1 h-10"
          />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-widest font-mono-ui">Starts</Label>
          <Input
            data-testid="bulk-starts"
            type="date"
            value={starts}
            onChange={(e) => setStarts(e.target.value)}
            className="mt-1 h-10"
          />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-widest font-mono-ui">Expires</Label>
          <Input
            data-testid="bulk-expires"
            type="date"
            value={expires}
            onChange={(e) => setExpires(e.target.value)}
            className="mt-1 h-10"
          />
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        <Button
          data-testid="bulk-apply"
          onClick={apply}
          disabled={busy}
          className="h-10 rounded-full bg-accent text-accent-foreground hover:bg-primary hover:text-primary-foreground text-sm font-semibold px-6"
        >
          <Tag className="w-4 h-4 mr-2" />
          Apply to all "{category}"
        </Button>
        <Button
          data-testid="bulk-clear"
          onClick={clear}
          disabled={busy}
          variant="outline"
          className="h-10 rounded-full text-destructive border-destructive/40 hover:bg-destructive hover:text-destructive-foreground px-4"
        >
          <Trash2 className="w-4 h-4 mr-2" /> Clear all in "{category}"
        </Button>
      </div>
    </div>
  );
};

const OfferCard = ({ product, busy, onSave, onClear }) => {
  const [discount, setDiscount] = useState(product.discount_percent || 0);
  const [label, setLabel] = useState(product.offer_label || "");
  const [expires, setExpires] = useState(product.offer_expires_at || "");

  useEffect(() => {
    setDiscount(product.discount_percent || 0);
    setLabel(product.offer_label || "");
    setExpires(product.offer_expires_at || "");
  }, [product]);

  const hasOffer = (product.discount_percent || 0) > 0;
  const discountedInr = product.discount_percent
    ? inr(product.price * (1 - product.discount_percent / 100))
    : null;

  const save = () => {
    const dp = parseFloat(discount);
    if (isNaN(dp) || dp < 0 || dp > 90) return toast.error("Discount must be 0-90%");
    onSave({
      discount_percent: dp,
      offer_label: label,
      offer_expires_at: expires,
    });
  };

  return (
    <article
      data-testid={`offer-card-${product.id}`}
      className={`rounded-2xl border p-5 bg-card ${
        hasOffer ? "border-accent" : "border-border"
      }`}
    >
      <div className="flex items-start gap-3">
        <img src={product.image} alt="" className="w-14 h-14 rounded-lg object-cover shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="font-display text-base font-semibold leading-tight truncate">
            {product.name}
          </div>
          <div className="mt-1 font-mono-ui text-xs text-muted-foreground uppercase tracking-wider">
            {product.category} · {product.unit}
          </div>
        </div>
        <div className="text-right">
          <div className={`font-mono-ui text-sm ${hasOffer ? "line-through text-muted-foreground" : "text-primary font-semibold"}`}>
            {inr(product.price)}
          </div>
          {hasOffer && (
            <div className="font-mono-ui text-lg font-semibold text-accent-foreground">
              {discountedInr}
            </div>
          )}
        </div>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-3">
        <div>
          <Label className="text-[10px] uppercase tracking-widest font-mono-ui">Discount %</Label>
          <Input
            data-testid={`offer-discount-${product.id}`}
            type="number"
            min="0"
            max="90"
            step="1"
            value={discount}
            onChange={(e) => setDiscount(e.target.value)}
            className="mt-1 h-10"
          />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-widest font-mono-ui">Expires (optional)</Label>
          <Input
            data-testid={`offer-expires-${product.id}`}
            type="date"
            value={expires}
            onChange={(e) => setExpires(e.target.value)}
            className="mt-1 h-10"
          />
        </div>
      </div>
      <div className="mt-3">
        <Label className="text-[10px] uppercase tracking-widest font-mono-ui">Offer label</Label>
        <Input
          data-testid={`offer-label-${product.id}`}
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder='e.g. "Diwali 15% OFF"'
          className="mt-1 h-10"
        />
      </div>

      <div className="mt-4 flex gap-2">
        <Button
          data-testid={`offer-save-${product.id}`}
          onClick={save}
          disabled={busy}
          className="flex-1 h-10 rounded-full bg-primary text-primary-foreground hover:bg-accent hover:text-accent-foreground text-sm font-semibold"
        >
          <Save className="w-4 h-4 mr-2" /> Save offer
        </Button>
        {hasOffer && (
          <Button
            data-testid={`offer-clear-${product.id}`}
            onClick={onClear}
            disabled={busy}
            variant="outline"
            className="h-10 rounded-full text-destructive border-destructive/40 hover:bg-destructive hover:text-destructive-foreground"
          >
            <Trash2 className="w-4 h-4 mr-2" /> Clear
          </Button>
        )}
      </div>
    </article>
  );
};
